import React, { useState, useEffect, useCallback } from 'react';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonText,
} from '@ionic/react';
import { alertCircleOutline, closeCircleOutline, informationCircleOutline, refreshOutline } from 'ionicons/icons';
import ErrorLoggingService from '../service/ErrorLoggingService';
import './ErrorMessage.css';

/**
 * Comprehensive Error Message Component
 * Displays user-friendly error messages with recovery guidance and actions
 */
const ErrorMessage = ({ 
  error, 
  onDismiss, 
  onRetry, 
  showDetails = false,
  autoHide = false,
  autoHideDelay = 5000,
  className = '',
}) => {
  const [showFullDetails, setShowFullDetails] = useState(showDetails);
  const [isVisible, setIsVisible] = useState(true);

  // Parse error to get structured information - with safety fallback
  let errorInfo;
  try {
    errorInfo = parseError(error);
    // Ensure recoveryGuidance is always an array
    if (!Array.isArray(errorInfo.recoveryGuidance)) {
      errorInfo.recoveryGuidance = getDefaultRecoveryGuidance();
    }
  } catch (parseErr) {
    // If parsing fails, use default error info
    console.error('Error parsing error object:', parseErr);
    errorInfo = {
      userMessage: 'An unexpected error occurred',
      severity: 'error',
      type: 'UNKNOWN',
      retryable: false,
      recoveryGuidance: getDefaultRecoveryGuidance(),
    };
  }

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  }, [onDismiss]);

  // Auto-hide if enabled
  useEffect(() => {
    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay, isVisible, handleDismiss]);

  // Log error when component mounts
  useEffect(() => {
    if (error) {
      ErrorLoggingService.error(
        errorInfo.userMessage,
        error,
        { component: 'ErrorMessage', errorType: errorInfo.type }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    handleDismiss();
  };

  // Always render if errorInfo exists, even if error is falsy
  // This prevents crashes when error is undefined/null
  if (!isVisible) {
    return null;
  }
  
  // If error is falsy but component is visible, show default error
  if (!error) {
    return null; // Don't show error message if no error provided
  }

  return (
    <div className={`error-message-container ${className} ${errorInfo.severity}`}>
      <div className="error-message-header">
        <IonIcon 
          icon={errorInfo.severity === 'critical' ? alertCircleOutline : closeCircleOutline} 
          className="error-icon"
        />
        <IonText className="error-title">{errorInfo.userMessage}</IonText>
        <IonButton 
          fill="clear" 
          size="small" 
          onClick={handleDismiss}
          className="error-dismiss-button"
        >
          <IonIcon icon={closeCircleOutline} />
        </IonButton>
      </div>

      {errorInfo.recoveryGuidance && Array.isArray(errorInfo.recoveryGuidance) && errorInfo.recoveryGuidance.length > 0 && (
        <div className="error-recovery-section">
          <div className="error-recovery-header">
            <IonIcon icon={informationCircleOutline} className="recovery-icon" />
            <IonText className="recovery-title">What you can do:</IonText>
          </div>
          <ul className="recovery-steps">
            {errorInfo.recoveryGuidance.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
        </div>
      )}

      {errorInfo.retryable && onRetry && (
        <div className="error-actions">
          <IonButton 
            fill="solid" 
            color="primary" 
            onClick={handleRetry}
            className="error-retry-button"
            style={{ '--color': '#000000', color: '#000000' }}
          >
            <IonIcon icon={refreshOutline} slot="start" style={{ color: '#000000' }} />
            <span style={{ color: '#000000' }}>Try Again</span>
          </IonButton>
        </div>
      )}

      {showFullDetails && errorInfo.technicalDetails && (
        <div className="error-technical-details">
          <IonItem button onClick={() => setShowFullDetails(!showFullDetails)}>
            <IonLabel>
              <h3>Technical Details</h3>
              <p className="error-technical-text">{errorInfo.technicalDetails}</p>
            </IonLabel>
          </IonItem>
        </div>
      )}

      {!showFullDetails && errorInfo.technicalDetails && (
        <div className="error-toggle-details">
          <IonButton 
            fill="clear" 
            size="small" 
            onClick={() => setShowFullDetails(true)}
            style={{ '--color': '#ffffff', color: '#ffffff' }}
          >
            <span style={{ color: '#ffffff' }}>Show Technical Details</span>
          </IonButton>
        </div>
      )}
    </div>
  );
};

/**
 * Parse error object to extract structured information
 */
function parseError(error) {
  // Handle null/undefined explicitly with better type checking
  if (!error || (typeof error !== 'string' && typeof error !== 'object')) {
    return {
      userMessage: 'An unexpected error occurred',
      severity: 'error',
      type: 'UNKNOWN',
      retryable: false,
      recoveryGuidance: [
        'Please refresh the page and try again',
        'If the problem persists, contact support'
      ],
    };
  }

  // Handle HTTP errors
  if (error.status || error.statusCode) {
    return parseHttpError(error);
  }

  // Handle Error objects
  if (error instanceof Error) {
    return parseErrorObject(error);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      userMessage: error,
      severity: 'error',
      type: 'UNKNOWN',
      retryable: true,
      recoveryGuidance: getDefaultRecoveryGuidance(),
      technicalDetails: error,
    };
  }

  // Handle object errors - add safety checks
  if (typeof error === 'object' && error !== null) {
    // Ensure recoveryGuidance is always an array
    const recoveryGuidance = Array.isArray(error.recoveryGuidance) 
      ? error.recoveryGuidance 
      : getDefaultRecoveryGuidance();
    
    return {
      userMessage: error.message || error.errorMessage || 'An error occurred',
      severity: error.severity || 'error',
      type: error.type || 'UNKNOWN',
      retryable: error.retryable !== false,
      recoveryGuidance: recoveryGuidance,
      technicalDetails: JSON.stringify(error, null, 2),
    };
  }

  return {
    userMessage: 'An unexpected error occurred',
    severity: 'error',
    type: 'UNKNOWN',
    retryable: false,
    recoveryGuidance: getDefaultRecoveryGuidance(),
  };
}

/**
 * Parse HTTP error responses
 */
function parseHttpError(error) {
  // Add safety checks for undefined/null error
  if (!error || (typeof error !== 'object')) {
    return {
      userMessage: 'An HTTP error occurred',
      severity: 'error',
      type: 'HTTP_ERROR',
      retryable: true,
      recoveryGuidance: getDefaultRecoveryGuidance(),
      technicalDetails: 'Unknown HTTP error',
    };
  }
  
  const status = error?.status || error?.statusCode || 500;
  const statusText = error?.statusText || '';
  const message = error?.message || error?.errorMessage || statusText;

  const errorMap = {
    400: {
      userMessage: 'Invalid request. Please check your input and try again.',
      severity: 'error',
      type: 'VALIDATION',
      retryable: true,
      recoveryGuidance: [
        'Verify all required fields are filled correctly',
        'Check for any formatting errors',
        'Ensure all data is valid before submitting'
      ],
    },
    401: {
      userMessage: 'Your session has expired. Please log in again.',
      severity: 'error',
      type: 'AUTHENTICATION',
      retryable: false,
      recoveryGuidance: [
        'Click the logout button and log in again',
        'If the problem persists, clear your browser cache',
        'Contact support if you continue to have issues'
      ],
    },
    403: {
      userMessage: 'You do not have permission to perform this action.',
      severity: 'error',
      type: 'AUTHORIZATION',
      retryable: false,
      recoveryGuidance: [
        'Contact your administrator to request access',
        'Verify you are logged in with the correct account'
      ],
    },
    404: {
      userMessage: 'The requested resource was not found.',
      severity: 'error',
      type: 'NOT_FOUND',
      retryable: true,
      recoveryGuidance: [
        'Verify the resource exists',
        'Check the URL or identifier',
        'Try refreshing the page'
      ],
    },
    408: {
      userMessage: 'The request took too long. Please try again.',
      severity: 'error',
      type: 'TIMEOUT',
      retryable: true,
      recoveryGuidance: [
        'Check your internet connection',
        'Try again in a few moments',
        'If the problem persists, the server may be busy'
      ],
    },
    429: {
      userMessage: 'Too many requests. Please wait a moment and try again.',
      severity: 'warn',
      type: 'RATE_LIMIT',
      retryable: true,
      recoveryGuidance: [
        'Wait a few seconds before trying again',
        'Reduce the frequency of your requests'
      ],
    },
    500: {
      userMessage: 'A server error occurred. Our team has been notified.',
      severity: 'critical',
      type: 'SERVER_ERROR',
      retryable: true,
      recoveryGuidance: [
        'Try again in a few moments',
        'If the problem persists, contact support',
        'Check our status page for known issues'
      ],
    },
    502: {
      userMessage: 'Service temporarily unavailable. Please try again later.',
      severity: 'error',
      type: 'BAD_GATEWAY',
      retryable: true,
      recoveryGuidance: [
        'Wait a few moments and try again',
        'Check your internet connection',
        'Contact support if the issue continues'
      ],
    },
    503: {
      userMessage: 'Service is temporarily unavailable for maintenance.',
      severity: 'warn',
      type: 'SERVICE_UNAVAILABLE',
      retryable: true,
      recoveryGuidance: [
        'Wait a few moments and try again',
        'Check our status page for maintenance updates'
      ],
    },
  };

  const errorInfo = errorMap[status] || {
    userMessage: `An error occurred (${status}): ${message}`,
    severity: status >= 500 ? 'critical' : 'error',
    type: 'HTTP_ERROR',
    retryable: status < 500,
    recoveryGuidance: getDefaultRecoveryGuidance(),
  };

  return {
    ...errorInfo,
    technicalDetails: `HTTP ${status} ${statusText}: ${message}`,
  };
}

/**
 * Parse Error object
 */
function parseErrorObject(error) {
  const message = error.message || 'An unexpected error occurred';
  
  // Network errors
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) {
    return {
      userMessage: 'Unable to connect to the server. Please check your internet connection.',
      severity: 'error',
      type: 'NETWORK',
      retryable: true,
      recoveryGuidance: [
        'Check your internet connection',
        'Verify you are not behind a firewall or proxy',
        'Try again in a few moments',
        'Contact support if the problem persists'
      ],
      technicalDetails: error.stack || message,
    };
  }

  // Timeout errors
  if (message.includes('timeout')) {
    return {
      userMessage: 'The request timed out. Please try again.',
      severity: 'error',
      type: 'TIMEOUT',
      retryable: true,
      recoveryGuidance: [
        'Check your internet connection speed',
        'Try again in a few moments',
        'If the problem persists, the server may be busy'
      ],
      technicalDetails: error.stack || message,
    };
  }

  return {
    userMessage: message,
    severity: 'error',
    type: 'ERROR',
    retryable: true,
    recoveryGuidance: getDefaultRecoveryGuidance(),
    technicalDetails: error.stack || message,
  };
}

/**
 * Get default recovery guidance
 */
function getDefaultRecoveryGuidance() {
  return [
    'Refresh the page and try again',
    'Clear your browser cache and cookies',
    'If the problem persists, contact support'
  ];
}

export default ErrorMessage;

